import ExcelJS from 'exceljs';
import { LastCargoesReportData } from '../ReportGeneratorTypes';
import Decimal from 'decimal.js';
import { generateReportHeaderExcel } from './reportHeader';
import { getCoatingName } from 'src/utils/stowage/tankUtils';
import { getFormattedFillRatio, getLetterForNumber, getOrdinalSuffix, setHeaderStyle } from '../excelReportUtils';
import sortUtils from 'src/utils/sortUtils';

const DATA_START_ROW = 7;

/**
 * Generates an Excel report showing the last cargoes for each tank
 * @param workbook - ExcelJS workbook instance
 * @param data - Report data containing tank cargo history information
 * @param logoImageId - ID of the logo image in the workbook
 */
export function generateLastCargoesReportExcel(workbook: ExcelJS.Workbook, data: LastCargoesReportData, logoImageId: number): void {
    const { numberOfSteps, selectedBerthCall, cargoNameType } = data;

    const _worksheet = workbook.addWorksheet('Last Cargoes');

    let _title = `${numberOfSteps} Last Cargoes in ${data.selectedPortCall?.voyageNumber} ${data.selectedPortCall?.portDisplayName ?? ''}`;
    if (selectedBerthCall && selectedBerthCall.berthName) {
        _title += ` - ${selectedBerthCall.berthName}`;
    }

    generateReportHeaderExcel(_worksheet, data.vessel.vesselName, _title, data.selectedCharterers, logoImageId, 11);

    let currentRow = DATA_START_ROW;

    const _headerRow = _worksheet.getRow(currentRow);
    const _subHeaderRow = _worksheet.getRow(currentRow + 1);

    _headerRow.height = 20;
    _subHeaderRow.height = 20;
    let _columnIndex = 1;

    // First header group - tank information
    const tankHeaderCell = _headerRow.getCell(_columnIndex);
    tankHeaderCell.value = 'Tank';
    tankHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    const coatingHeaderCell = _headerRow.getCell(_columnIndex);
    coatingHeaderCell.value = 'Coating';
    coatingHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    const volumeHeaderCell = _headerRow.getCell(_columnIndex);
    volumeHeaderCell.value = '98% Vol.';
    volumeHeaderCell.style = {
        alignment: {
            vertical: 'middle',
            horizontal: 'center',
        },
    };
    _worksheet.mergeCells(currentRow, _columnIndex, currentRow + 1, _columnIndex);
    _columnIndex++;

    // Apply border after base columns
    for (let i = 1; i <= _columnIndex - 1; i++) {
        setHeaderStyle(_headerRow.getCell(i));
        setHeaderStyle(_subHeaderRow.getCell(i));
    }

    // Add right border to the last base column
    _headerRow.getCell(_columnIndex - 1).border = {
        ..._headerRow.getCell(_columnIndex - 1).border,
        right: { style: 'medium' },
    };
    _subHeaderRow.getCell(_columnIndex - 1).border = {
        ..._subHeaderRow.getCell(_columnIndex - 1).border,
        right: { style: 'medium' },
    };

    // Add cargo column groups - each cargo has 3 columns (Trade Name, UN No, Fill ratio)
    for (let i = 0; i < numberOfSteps; i++) {
        const _cargoHeader = i === 0 ? 'Previous Cargo' : `${i + 1}${getOrdinalSuffix(i + 1)} Previous Cargo`;

        // Merge cargo header cells
        _worksheet.mergeCells(currentRow, _columnIndex, currentRow, _columnIndex + 2);
        _headerRow.getCell(_columnIndex).value = _cargoHeader;

        // Set subheaders
        _subHeaderRow.getCell(_columnIndex).value = cargoNameType === 'tradeName' ? 'Trade Name' : 'Product Name';
        _subHeaderRow.getCell(_columnIndex + 1).value = 'UN No.';
        _subHeaderRow.getCell(_columnIndex + 2).value = 'Fill ratio';

        // Style cargo headers
        for (let j = 0; j < 3; j++) {
            setHeaderStyle(_headerRow.getCell(_columnIndex + j));
            setHeaderStyle(_subHeaderRow.getCell(_columnIndex + j));
        }

        // Add right border to the last column in this cargo group
        _headerRow.getCell(_columnIndex + 2).border = {
            ..._headerRow.getCell(_columnIndex + 2).border,
            right: { style: 'medium' },
        };
        _subHeaderRow.getCell(_columnIndex + 2).border = {
            ..._subHeaderRow.getCell(_columnIndex + 2).border,
            right: { style: 'medium' },
        };

        _columnIndex += 3;
    }

    // Move to data rows (after header and subheader)
    currentRow += 2;

    const tanks = data.selectedTankIds
        ? data.vessel.cargoTanks.filter((tank) => data.selectedTankIds?.includes(tank.tankId))
        : data.vessel.cargoTanks;

    const { compareTankNamesForLastCargoes } = sortUtils();

    // Add tank data rows
    for (const _tank of tanks.sort((a, b) => compareTankNamesForLastCargoes(a.tankName, b.tankName))) {
        const _dataRow = _worksheet.getRow(currentRow);
        _dataRow.height = 16;

        const coatingName = getCoatingName(_tank.coatingType);

        _dataRow.getCell(1).value = _tank.tankName;
        _dataRow.getCell(2).value = coatingName ?? '';
        _dataRow.getCell(3).value = _tank.volumeAt98Percent;

        // Add right border to the last base column
        _dataRow.getCell(3).border = {
            ..._dataRow.getCell(3).border,
            right: { style: 'medium' },
        };

        // set value format of cell 3 to number
        _dataRow.getCell(3).numFmt = '#,##0.00';

        const _cargoContents = data.cargoContentsPerTank.find((_cargoContent) => _cargoContent.tankId === _tank.tankId);

        // Add cargo data
        let _colIndex = 4;
        for (let i = 0; i < numberOfSteps; i++) {
            const _cargo = _cargoContents?.cargoContent[i];
            if (_cargo) {
                const weight = new Decimal(_cargo.stowage.weight);
                _dataRow.getCell(_colIndex).value =
                    cargoNameType === 'tradeName' ? _cargo.cargo?.product?.tradeName : _cargo.cargo?.product?.category?.name;
                _dataRow.getCell(_colIndex + 1).value = _cargo.cargo.product.unCode; // Product UN No.
                _dataRow.getCell(_colIndex + 2).value =
                    _cargo.stowage.weight !== undefined ? getFormattedFillRatio(weight, data.stowagePlan, _cargo, _tank) ?? '' : '';
                _dataRow.getCell(_colIndex + 2).numFmt = '0.00%';
            } else {
                _dataRow.getCell(_colIndex).value = '';
                _dataRow.getCell(_colIndex + 1).value = '';
                _dataRow.getCell(_colIndex + 2).value = '';
            }

            // Add right border to the last column in each cargo group
            _dataRow.getCell(_colIndex + 2).border = {
                ..._dataRow.getCell(_colIndex + 2).border,
                right: { style: 'medium' },
            };

            _colIndex += 3;
        }

        currentRow++;
    }

    // Set column widths
    _worksheet.getColumn(1).width = 10; // Tank
    _worksheet.getColumn(2).width = 10; // Coating
    _worksheet.getColumn(3).width = 10; // 98% Vol

    // Set cargo column widths
    for (let i = 0; i < numberOfSteps; i++) {
        _worksheet.getColumn(4 + i * 3).width = 30; // Trade Name
        _worksheet.getColumn(5 + i * 3).width = 10; // UN No.
        _worksheet.getColumn(6 + i * 3).width = 10; // Fill ratio
    }

    // Add borders to table
    const _lastRow = currentRow - 1;
    for (let i = DATA_START_ROW; i <= _lastRow; i++) {
        for (let j = 0; j < _columnIndex - 1; j++) {
            _worksheet.getCell(`${getLetterForNumber(j)}${i}`).border = {
                ..._worksheet.getCell(`${getLetterForNumber(j)}${i}`).border,
                top: { style: 'thin' },
                bottom: { style: 'thin' },
            };
        }
    }

    // Add bottom border to the last row
    for (let j = 0; j < _columnIndex - 1; j++) {
        _worksheet.getCell(`${getLetterForNumber(j)}${_lastRow}`).border = {
            ..._worksheet.getCell(`${getLetterForNumber(j)}${_lastRow}`).border,
            bottom: { style: 'medium' },
        };
    }

    _worksheet.views = [
        { state: 'frozen', xSplit: 0, ySplit: DATA_START_ROW + 1, topLeftCell: `A${DATA_START_ROW + 2}`, activeCell: `A${DATA_START_ROW + 2}` },
    ];
}
